import React, {useMemo} from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import {Bar} from 'react-chartjs-2';
// eslint-disable-next-line import/no-unresolved
import {
	Chart as ChartJS,
	CategoryScale,
	LinearScale,
	TimeScale,
	PointElement,
	LineElement,
	Title,
	Tooltip,
	Legend,
	Filler,
} from 'chart.js';
import {transparentize} from 'polished';

ChartJS.register(CategoryScale, LinearScale, TimeScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);
import 'chartjs-adapter-moment';

const AFFIX_POSITION = {
	PREFIX: 'PREFIX',
	SUFFIX: 'SUFFIX',
};

export const Chart = ({dataSets = [], yAxisAffix, modifyOptions = options => options, usePointStyle = false}) => {
	// used for setting timeperiod in config
	let earliestDate = moment.max();
	let latestDate = moment.min();

	const setMinMax = date => {
		if (earliestDate.isAfter(date)) {
			earliestDate = date;
		}
		if (latestDate.isBefore(date)) {
			latestDate = date;
		}
	};

	const formatDataEntry = data => {
		setMinMax(data.date);
		return {x: data.date.format('YYYY/MM/DD'), y: data.value, customData: data.customData};
	};

	const formmattedDataSets = useMemo(
		() =>
			dataSets
				.map(dataSet => ({
					type: dataSet.type || 'line',
					label: dataSet.name,
					order: dataSet.order,
					borderDash: dataSet.dashed ? [5, 5] : undefined,
					borderDashOffset: dataSet.borderDashOffset,
					borderColor: dataSet.color,
					backgroundColor: dataSet.backgroundColor || transparentize(0.6, dataSet.color),
					pointStyle: dataSet.pointStyle || (usePointStyle ? 'line' : undefined),
					pointHoverRadius: dataSet.pointHoverRadius || 0,
					pointHoverBorderWidth: dataSet.pointHoverBorderWidth || 0,
					pointHoverBackgroundColor: dataSet.pointHoverBackgroundColor || '#fff',
					pointRadius: dataSet.pointRadius || 0,
					fill: dataSet.filled === true ? 'origin' : dataSet.filled || false,
					borderWidth: dataSet.borderWidth || 2,
					hideLegend: dataSet.hideLegend,
					hideLabel: dataSet.hideLabel,
					data: dataSet.data.map(formatDataEntry),
				}))
				.reverse(),
		[dataSets]
	);

	const tooltipLinePlugin = {
		afterDatasetsDraw: function (chart) {
			if (chart.tooltip._active && chart.tooltip._active.length) {
				var activePoint = chart.tooltip._active[0],
					ctx = chart.ctx,
					y_axis = chart.scales.y,
					x = activePoint.element.x,
					topY = y_axis.top,
					bottomY = y_axis.bottom;
				// draw line
				ctx.save();
				ctx.beginPath();
				ctx.moveTo(x, topY);
				ctx.lineTo(x, bottomY);
				ctx.lineWidth = 1;
				ctx.strokeStyle = '#e6e6e6';
				ctx.stroke();
				ctx.restore();
			}
		},
	};

	const getOptions = (yAxisAffix, earliestDate, latestDate) => {
		// calculate time.period
		let timeUnit = 'day';
		if (latestDate.diff(earliestDate, 'months', true) > 2) {
			timeUnit = 'month';
		} else if (latestDate.diff(earliestDate, 'months', true) > 1) {
			timeUnit = 'week';
		}

		const formatLabel = (label, affix) => {
			if (!affix) {
				return label;
			}
			if (affix.position === AFFIX_POSITION.PREFIX) {
				return `${affix.value}${label}`;
			}
			if (affix.position === AFFIX_POSITION.SUFFIX) {
				return `${label}${affix.value}`;
			}

			throw Error('Affix object not formatted correctly');
		};

		const tooltip = {
			mode: 'index',
			intersect: false,
			backgroundColor: '#fff',
			borderColor: '#e6e6e6',
			borderWidth: 1,
			titleColor: '#535353',
			bodyColor: '#535353',
			footerColor: '#535353',
			padding: 12,
			usePointStyle,
			callbacks: {
				title: ctx => {
					const date = moment(ctx[0].label);
					return date.format('DD MMMM, YYYY');
				},
				label: ctx => {
					const dataSetLabel = ctx.dataset.label ? `${ctx.dataset.label}: ` : '';
					return `${dataSetLabel}${formatLabel(ctx.parsed.y, yAxisAffix)}`;
				},
			},
		};

		const options = {
			budgetHeightHack: true,
			font: {
				family: 'neue-haas-grotesk-text',
			},
			elements: {
				line: {
					tension: 0,
				},
			},
			layout: {
				padding: {
					left: 0,
					right: 0,
					top: 0,
					bottom: 0,
				},
			},
			animation: {
				easing: 'easeInOutBack',
			},
			plugins: {
				tooltip,
				filler: {
					propagate: true,
				},
				datalabels: {
					display: false,
				},
				legend: {
					position: 'bottom',
					labels: {
						usePointStyle,
						filter: (legendItem, data) => {
							return !data.datasets[legendItem.datasetIndex].hideLegend;
						},
					},
				},
			},
			hover: {
				intersect: false,
			},
			scales: {
				y: {
					beginAtZero: true,
					ticks: {
						font: {
							color: 'rgba(0,0,0,0.5)',
						},
						maxTicksLimit: 5,
						padding: 20,
						callback: label => formatLabel(label, yAxisAffix),
					},
					grid: {
						drawTicks: false,
						display: true,
						tickBorderDash: [8, 4],
					},
				},
				x: {
					type: 'time',
					time: {
						unit: timeUnit,
						displayFormats: {
							day: 'DD MMM ',
							week: 'DD MMM ',
							month: 'DD MMM ',
						},
					},
					grid: {
						display: false,
					},
					ticks: {
						font: {
							color: 'rgba(0,0,0,0.5)',
							size: 11,
						},
						padding: 0,
					},
				},
			},
			responsive: true,
			maintainAspectRatio: false,
		};

		return modifyOptions(options);
	};

	const dataConfig = {
		// labels: xAxisLabels,
		datasets: formmattedDataSets,
	};

	return (
		<div style={{position: 'relative', height: '100%', width: '100%'}}>
			<Bar data={dataConfig} options={getOptions(yAxisAffix, earliestDate, latestDate)} plugins={[tooltipLinePlugin]} />
		</div>
	);
};

Chart.register = ChartJS.register;

Chart.propTypes = {
	dataSets: PropTypes.arrayOf(
		PropTypes.shape({
			type: PropTypes.string,
			name: PropTypes.string.isRequired,
			color: PropTypes.string.isRequired,
			filled: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
			data: PropTypes.arrayOf(
				PropTypes.shape({
					date: PropTypes.object.isRequired,
					value: PropTypes.number,
					customData: PropTypes.object,
				})
			).isRequired,
		})
	).isRequired,
	usePointStyle: PropTypes.bool,
	yAxisAffix: PropTypes.shape({
		value: PropTypes.string.isRequired,
		position: PropTypes.oneOf([AFFIX_POSITION.PREFIX, AFFIX_POSITION.SUFFIX]).isRequired,
	}),
	/**
	 * Gives the option to modify the ChartJS options directly. Input to the function
	 * is the pre-defined options and the function should return the modified options.
	 */
	modifyOptions: PropTypes.func.isRequired,
};
